import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ThumbsUp, ThumbsDown, BookOpen } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useGuideRating, useRateContent, useIncrementViewCount } from '../hooks/useGuides';
import type { GuideTerm } from '../types';

interface GuideTermModalProps {
  term: GuideTerm;
  onClose: () => void;
}

export function GuideTermModal({ term, onClose }: GuideTermModalProps) {
  const { t } = useTranslation('guides');
  const { data: userRating } = useGuideRating('term', term.id);
  const rateContent = useRateContent();
  const incrementView = useIncrementViewCount();

  // Increment view count on mount
  useEffect(() => {
    incrementView.mutate({ contentType: 'term', contentId: term.id });
  }, [term.id]);

  const handleRate = (isHelpful: boolean) => {
    rateContent.mutate({
      contentType: 'term',
      contentId: term.id,
      isHelpful,
    });
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-dark/25 rounded-xl flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-pp-orange" />
              </div>
              <div>
                <h2 className="!text-lg !font-semibold text-white">
                  {term.term}
                </h2>
                {term.category && (
                  <p className="text-xs text-white/50">{term.category.name}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content">
            <div className="glass-effect rounded-[15px] p-4">
              <h3 className="text-sm font-medium text-pp-orange mb-2">
                {t('termModal.simpleExplanation')}
              </h3>
              <p className="text-white/90 leading-relaxed whitespace-pre-wrap">
                {term.explanation}
              </p>
            </div>

            {/* Keywords */}
            {term.keywords && term.keywords.length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">
                  {t('termModal.tags')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {term.keywords.map((keyword, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-white/10 text-white/70 text-xs rounded-[5px] border border-white/20"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer with Rating */}
          <div className="bottom-sheet-footer !flex-col !gap-2">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-white/70">
                {t('termModal.wasHelpful')}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleRate(true)}
                  disabled={rateContent.isPending}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl transition-all ${
                    userRating?.is_helpful === true
                      ? 'bg-pp-teal/30 text-pp-teal ring-2 ring-pp-teal'
                      : 'bg-white/10 text-white/70 hover:bg-pp-teal/20 hover:text-pp-teal border border-white/20'
                  }`}
                  title={userRating?.is_helpful === true ? t('termModal.ratedHelpful') : t('termModal.helpfulAria')}
                >
                  <ThumbsUp className="w-4 h-4" />
                  <span className="text-sm">{t('termModal.yes')}</span>
                </button>
                <button
                  onClick={() => handleRate(false)}
                  disabled={rateContent.isPending}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl transition-all ${
                    userRating?.is_helpful === false
                      ? 'bg-red-500/30 text-red-400 ring-2 ring-red-500'
                      : 'bg-white/10 text-white/70 hover:bg-red-500/20 hover:text-red-400 border border-white/20'
                  }`}
                  title={userRating?.is_helpful === false ? t('termModal.ratedNotHelpful') : t('termModal.notHelpfulAria')}
                >
                  <ThumbsDown className="w-4 h-4" />
                  <span className="text-sm">{t('termModal.no')}</span>
                </button>
              </div>
            </div>
            {userRating && (
              <p className="text-xs text-white/40 text-right w-full">
                {userRating.is_helpful
                  ? t('termModal.helpfulAria')
                  : t('termModal.notHelpfulAria')}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
